import React, { useState, useEffect, useCallback } from 'react';
import { useMfgApi } from './mfgApi';
import { Loader2, Download } from 'lucide-react';
import { MfgReadOnlyBadge } from './useMfgRole';

export function MfgDPR() {
  const mfg = useMfgApi();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [unitFilter, setUnitFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date };
      if (unitFilter) params.unit_id = unitFilter;
      const [dpr, u] = await Promise.all([mfg.get('/reports/dpr', params), mfg.get('/units')]);
      setData(dpr); setUnits(u.items || u || []);
    } catch {} finally { setLoading(false); }
  }, [date, unitFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mfg-dpr-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">Daily Production Report<MfgReadOnlyBadge /></h1>
        <div className="flex items-center gap-3">
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="input-sm" data-testid="dpr-date" />
          <select value={unitFilter} onChange={e => setUnitFilter(e.target.value)} className="input-sm" data-testid="dpr-unit-filter"><option value="">All Units</option>{units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
          <button data-testid="dpr-download-pdf" onClick={() => { const params = { date }; if (unitFilter) params.unit_id = unitFilter; mfg.getPdf('/reports/dpr.pdf', params); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-500 text-white text-xs font-bold hover:bg-indigo-600 transition-colors"><Download size={14} /> PDF</button>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : !data ? <p className="text-gray-400 text-center py-12">No data</p> : (
        <div className="space-y-4">
          {(data.units || []).map(u => (
            <div key={u.unit_id} className="glass-card p-4" data-testid={`dpr-unit-${u.unit_id}`}>
              <h3 className="text-sm font-bold text-gray-900 mb-2">{u.unit_name}</h3>
              <table className="w-full text-xs">
                <thead><tr className="text-[10px] uppercase text-gray-500 border-b"><th className="text-left py-1.5 px-2">Order</th><th className="text-left py-1.5 px-2">SKU</th><th className="text-right py-1.5 px-2">Produced (pcs)</th><th className="text-right py-1.5 px-2">Dispatched (ctns)</th></tr></thead>
                <tbody>{(u.orders || []).map((o, i) => (<tr key={i} className="border-b border-gray-50"><td className="py-1.5 px-2">{o.order_no}</td><td className="py-1.5 px-2">{o.sku_name}</td><td className="py-1.5 px-2 text-right font-semibold">{o.production_pieces}</td><td className="py-1.5 px-2 text-right font-semibold">{o.dispatch_cartons}</td></tr>))}
                  <tr className="bg-gray-50 font-bold"><td colSpan={2} className="py-1.5 px-2">Unit Total</td><td className="py-1.5 px-2 text-right">{u.total_production_pieces}</td><td className="py-1.5 px-2 text-right">{u.total_dispatch_cartons}</td></tr>
                </tbody>
              </table>
            </div>
          ))}
          {data.grand_totals && (
            <div className="glass-card p-4 bg-indigo-50 border-indigo-200" data-testid="dpr-grand-totals">
              <div className="flex gap-6 text-sm"><span className="font-bold text-gray-900">Grand Total:</span><span>Production: <strong>{data.grand_totals.total_production_pieces} pcs</strong></span><span>Dispatch: <strong>{data.grand_totals.total_dispatch_cartons} ctns</strong></span></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MfgMonthlyPlan() {
  const mfg = useMfgApi();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => { setLoading(true); try { setData(await mfg.get('/reports/monthly-plan', { month })); } catch {} finally { setLoading(false); } }, [month]);
  useEffect(() => { load(); }, [load]);

  const STATUS_BADGE = { pending: 'bg-gray-100 text-gray-600', in_production: 'bg-amber-50 text-amber-700', partially_dispatched: 'bg-blue-50 text-blue-700', completed: 'bg-emerald-50 text-emerald-700' };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mfg-monthly-plan-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">Monthly Plan<MfgReadOnlyBadge /></h1>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="input-sm" data-testid="monthly-plan-month" />
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="monthly-plan-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b"><th className="text-left py-2 px-3">Order</th><th className="text-left py-2 px-3">Client</th><th className="text-left py-2 px-3">SKU</th><th className="text-left py-2 px-3">Due</th><th className="text-right py-2 px-3">Planned</th><th className="text-right py-2 px-3">Produced</th><th className="text-right py-2 px-3">Dispatched</th><th className="text-right py-2 px-3">Balance</th><th className="text-center py-2 px-3">Status</th></tr></thead>
            <tbody>
              {(data?.orders || []).length === 0 ? <tr><td colSpan={9} className="text-center py-12 text-gray-400">No orders for this month.</td></tr> : (data.orders || []).map(o => {
                const pct = o.total_cartons > 0 ? Math.min(100, Math.round((o.produced_cartons||0)/o.total_cartons*100)) : 0;
                return (
                  <tr key={o.id} className="border-b border-gray-50">
                    <td className="py-2 px-3 font-bold">{o.order_no}</td><td className="py-2 px-3">{o.client_name}</td><td className="py-2 px-3">{o.sku_name}</td><td className="py-2 px-3">{o.delivery_due_date}</td>
                    <td className="py-2 px-3 text-right">{o.total_cartons}</td><td className="py-2 px-3 text-right">{o.produced_cartons||0}</td><td className="py-2 px-3 text-right">{o.dispatched_cartons||0}</td>
                    <td className="py-2 px-3 text-right font-semibold">{o.balance_cartons||0}</td>
                    <td className="py-2 px-3 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_BADGE[o.status]||''}`}>{(o.status||'pending').replace(/_/g,' ')}</span></td>
                  </tr>);
              })}
            </tbody>
          </table>
          {data?.totals && <div className="p-3 bg-gray-50 text-xs flex gap-6"><span className="font-bold">Totals:</span><span>Orders: {data.totals.total_orders}</span><span>Planned: {data.totals.total_cartons} ctns</span><span>Produced: {data.totals.produced_cartons} ctns</span><span>Dispatched: {data.totals.dispatched_cartons} ctns</span>{data.overdue_count > 0 && <span className="text-red-600 font-bold">Overdue: {data.overdue_count}</span>}</div>}
        </div>
      )}
    </div>
  );
}

export function MfgFGStock() {
  const mfg = useMfgApi();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => { mfg.get('/fg-stock').then(r => setData(r.items || r || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="mfg-fg-stock-page">
      <h1 className="text-xl font-extrabold text-gray-900 mb-5 flex items-center gap-2">Finished Goods Stock<MfgReadOnlyBadge /></h1>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="fg-stock-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b"><th className="text-left py-2 px-3">SKU</th><th className="text-right py-2 px-3">Opening</th><th className="text-right py-2 px-3">Produced</th><th className="text-right py-2 px-3">Dispatched</th><th className="text-right py-2 px-3 font-bold">Net Stock</th></tr></thead>
            <tbody>
              {data.length === 0 ? <tr><td colSpan={5} className="text-center py-12 text-gray-400">No stock data.</td></tr> : data.map(r => (
                <React.Fragment key={r.sku_id}>
                  <tr className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [r.sku_id]: !p[r.sku_id] }))} data-testid={`fg-row-${r.sku_id}`}>
                    <td className="py-2 px-3 font-bold">{r.sku_name} <span className="text-gray-400 text-xs">({r.sku_code})</span></td>
                    <td className="py-2 px-3 text-right">{r.opening_qty}</td><td className="py-2 px-3 text-right text-emerald-600">{r.production_qty}</td><td className="py-2 px-3 text-right text-blue-600">{r.dispatched_qty}</td>
                    <td className="py-2 px-3 text-right font-extrabold">{r.net_qty}</td>
                  </tr>
                  {expanded[r.sku_id] && (r.by_unit || []).map(u => (
                    <tr key={u.unit_id} className="bg-gray-50/50 text-xs">
                      <td className="py-1 px-3 pl-8 text-gray-500">↳ {u.unit_name}</td>
                      <td className="py-1 px-3 text-right">{u.opening_qty}</td><td className="py-1 px-3 text-right">{u.production_qty}</td><td className="py-1 px-3 text-right">{u.dispatched_qty}</td>
                      <td className="py-1 px-3 text-right font-semibold">{u.net_qty}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function MfgDispatchHistory() {
  const mfg = useMfgApi();
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [totals, setTotals] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await mfg.get('/reports/dispatch-history', params);
      setRows(r.items || r || []); setTotals(r.totals || {});
    } catch {} finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mfg-dispatch-history-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">Dispatch History<MfgReadOnlyBadge /></h1>
        <div className="flex items-center gap-2">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="input-sm" data-testid="dh-from" placeholder="From" />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="input-sm" data-testid="dh-to" placeholder="To" />
        </div>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="dispatch-history-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b"><th className="text-left py-2 px-3">Date</th><th className="text-left py-2 px-3">Order</th><th className="text-left py-2 px-3">Destination</th><th className="text-right py-2 px-3">Cartons</th><th className="text-right py-2 px-3">Pieces</th><th className="text-left py-2 px-3">Vehicle</th></tr></thead>
            <tbody>{rows.length === 0 ? <tr><td colSpan={6} className="text-center py-12 text-gray-400">No dispatches.</td></tr> : rows.map(r => (<tr key={r.id} className="border-b border-gray-50"><td className="py-2 px-3">{r.dispatch_date}</td><td className="py-2 px-3 font-medium">{r.order_no}</td><td className="py-2 px-3">{r.destination_name}</td><td className="py-2 px-3 text-right font-bold">{r.quantity_cartons}</td><td className="py-2 px-3 text-right">{r.quantity_pieces}</td><td className="py-2 px-3 text-gray-500">{r.vehicle_no || '—'}</td></tr>))}</tbody>
          </table>
          {(totals.total_cartons != null) && <div className="p-3 bg-gray-50 text-xs font-bold">Total: {totals.total_cartons} cartons · {totals.total_pieces} pieces</div>}
        </div>
      )}
    </div>
  );
}

export function MfgProductionHistory() {
  const mfg = useMfgApi();
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await mfg.get('/reports/production-history', params);
      setRows(r.items || r || []);
    } catch {} finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mfg-production-history-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">Production History<MfgReadOnlyBadge /></h1>
        <div className="flex items-center gap-2">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="input-sm" data-testid="ph-from" />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="input-sm" data-testid="ph-to" />
        </div>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="production-history-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b"><th className="text-left py-2 px-3">Date</th><th className="text-left py-2 px-3">Unit</th><th className="text-left py-2 px-3">SKU</th><th className="text-left py-2 px-3">Order</th><th className="text-right py-2 px-3">Qty (pcs)</th><th className="text-center py-2 px-3">QA</th><th className="text-left py-2 px-3">Shift</th></tr></thead>
            <tbody>{rows.length === 0 ? <tr><td colSpan={7} className="text-center py-12 text-gray-400">No entries.</td></tr> : rows.map(r => (<tr key={r.id} className="border-b border-gray-50"><td className="py-2 px-3">{r.production_date}</td><td className="py-2 px-3 font-medium">{r.unit_name}</td><td className="py-2 px-3">{r.sku_name}</td><td className="py-2 px-3">{r.order_no||'—'}</td><td className="py-2 px-3 text-right font-bold">{r.quantity_pieces}</td><td className="py-2 px-3 text-center">{r.qa_approved ? '✅' : '⏳'}</td><td className="py-2 px-3 text-gray-500">{r.shift||'—'}</td></tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
